import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import cx from 'classnames';
import Visualization, {SIZES} from 'components/Visualization';
import {selectCardById, selectHasSampleData} from 'state/card/cardSelectors';
import ChartDate from 'handlers/Cards/CardHandler/components/ChartDate';
import ChartTable from 'handlers/Cards/CardHandler/components/ChartTable';
import CardDrillPaging from './CardDrillPaging';
import SampleDataBanner from 'components/SampleDataBanner';
import EmbedCardToolbar from './EmbedCardToolbar';
import {selectIsCardSettingEnabled} from 'state/cardSetting/cardSettingSelectors';
import {CARD_SETTINGS} from 'app/CardConstants';
import {analyticsTrack} from 'utilities/analytics';
import {ADOBE_ANALYTICS_EVENTS} from 'app/Analytics';
import ExpandableCardFilters from 'handlers/Cards/CardHandler/components/ExpandableCardFilters';
import {
  selectAppliedCombinedFiltersByCardId,
  selectIsLoadingCardFilters,
  selectSegmentsByCardId,
  selectFilterGroupsByCardId,
  selectDateRangeFilterByCardId,
  selectDateGrainByCardId,
  selectOverrideDateRangeByCardId,
  selectDataControlFiltersByCardId,
  selectHasQuerystringError,
  selectFunctionOverridesByCardId,
} from 'state/cardFilter/cardFilterSelectors';
import {selectIsDavinci} from 'state/environment/environmentSelectors';
import {selectFrameStyles} from 'state/layoutFrame/layoutFrameSelectors';
import CardDrillManager from 'components/CardDrillManager';
import PFilterParsingError from 'components/PFilterParsingError';
import {clearQueryStringError} from 'state/cardFilter/cardFilterActions';
import {disableChartPicker} from 'state/cardDetail/cardDetailActions';
import styles from './EmbedCardDetails.css?module';
import {RpcContext} from 'components/RpcEndpoint';
import {REMOTE} from 'app/JsRpcConstants';
import {cleanFiltersForRpc} from '../../components/RpcEndpoint';

const toggleTable = ({isTableActive}) => ({isTableActive: !isTableActive});

class EmbedCardDetails extends React.Component {
  state = {
    isTableActive: false,
  };

  handleDrillStarted = filters => {
    this.context.applyMethod(REMOTE.V1_ON_DRILL, {filters});
  };

  componentDidUpdate(prevProps) {
    const {filters} = this.props;
    const prevFilters = prevProps.filters;
    if (
      prevFilters !== filters &&
      (!!prevFilters.length || !!filters.length) &&
      this.context !== undefined &&
      this.context.applyMethod !== undefined
    ) {
      this.context.applyMethod(REMOTE.V1_ON_FILTERS_CHANGE, {
        filters: cleanFiltersForRpc(filters),
      });
    }
  }

  handleDrillFinished = index => {
    analyticsTrack(ADOBE_ANALYTICS_EVENTS.PUBLIC__CARD__DRILL, {
      card_id: this.props.card.urn,
      drill_path_urn: this.props.card.drillPathURNs[index],
    });
  };

  handlePopDrillFinished = () => {
    analyticsTrack(ADOBE_ANALYTICS_EVENTS.PUBLIC__CARD__POP__DRILL, {
      card_id: this.props.card.urn,
    });
  };

  handleClearQueryStringError = () => {
    this.props.clearQueryStringError(this.props.cardId);
  };

  handleTableActionClick = () => {
    this.setState(toggleTable);
    this.props.disableChartPicker();
  };

  render() {
    const {
      card,
      cardId,
      pageId,
      viewId,
      filters,
      segments,
      filterGroupIds,
      dataControlFilters,
      hasSampleData,
      dateTimeElement,
      dateRangeFilter,
      overrideDateRange,
      functionOverrides,
      isTitleEnabled,
      isDavinci,
      layoutFrameStyles,
      fitToFrame,
      hasQuerystringError,
      isLoadingCardFilters,
      viewStyle,
      ...rest
    } = this.props;
    const {isTableActive} = this.state;
    if (!card || isLoadingCardFilters) return null;
    const cardTitle = isTitleEnabled ? card.title : null;
    const hasBackground =
      layoutFrameStyles.backgroundColor || layoutFrameStyles.backgroundImage;

    return (
      <CardDrillManager
        cardId={this.props.cardId}
        onDrillStarted={this.handleDrillStarted}
        onDrillFinished={this.handleDrillFinished}
        onPopDrillFinished={this.handlePopDrillFinished}
      >
        {({drillFilters, handleDrill, handlePopDrill}) => (
          <div className={isDavinci ? null : styles.container}>
            <div
              className={cx(styles.mainVisualization, {
                [styles.hidden]: drillFilters.length,
                [styles.hasBackground]: hasBackground,
              })}
              style={layoutFrameStyles}
            >
              {!this.props.isDavinci ? (
                <div className={styles.cardHeader}>
                  {cardTitle ? (
                    <span className={styles.title}>{cardTitle}</span>
                  ) : null}
                  <EmbedCardToolbar
                    isTableActive={isTableActive}
                    filters={filters}
                    onTableActionClick={this.handleTableActionClick}
                    cardId={cardId}
                  />
                </div>
              ) : null}
              <PFilterParsingError
                clearQueryStringError={this.handleClearQueryStringError}
                hasQuerystringError={hasQuerystringError}
              />
              <ExpandableCardFilters cardId={cardId} />
              <ChartDate cardId={cardId} />
              {isTableActive ? (
                <ChartTable
                  cardId={cardId}
                  filters={filters}
                  segments={segments}
                  filterGroupIds={filterGroupIds}
                  dataControlFilters={dataControlFilters}
                  dateRangeFilter={dateRangeFilter}
                  dateTimeElement={dateTimeElement}
                  overrideDateRange={overrideDateRange}
                  functionOverrides={functionOverrides}
                />
              ) : (
                <Visualization
                  size={SIZES.DETAILS}
                  cardId={cardId}
                  pageId={pageId}
                  viewId={viewId}
                  filters={filters}
                  segments={segments}
                  filterGroupIds={filterGroupIds}
                  dataControlFilters={dataControlFilters}
                  dateRangeFilter={dateRangeFilter}
                  dateTimeElement={dateTimeElement}
                  overrideDateRange={overrideDateRange}
                  functionOverrides={functionOverrides}
                  onDrill={handleDrill}
                  hasBackground={hasBackground}
                  textColor={layoutFrameStyles.color}
                  darkMode={layoutFrameStyles.darkMode}
                  fitToFrame={fitToFrame}
                  viewStyle={viewStyle}
                  {...rest}
                />
              )}
            </div>
            {drillFilters.map((_, index, array) => (
              <CardDrillPaging
                className={cx(styles.mainVisualization, {
                  [styles.hidden]: index < array.length - 1,
                })}
                key={index}
                index={index}
                card={card}
                filters={filters}
                segments={segments}
                dateRangeFilter={dateRangeFilter}
                dateTimeElement={dateTimeElement}
                overrideDateRange={overrideDateRange}
                functionOverrides={functionOverrides}
                filterGroupIds={filterGroupIds}
                dataControlFilters={dataControlFilters}
                drillFilters={drillFilters}
                onDrill={handleDrill}
                popDrill={handlePopDrill}
                style={layoutFrameStyles}
              />
            ))}
            {hasSampleData ? <SampleDataBanner onClick={() => {}} /> : null}
          </div>
        )}
      </CardDrillManager>
    );
  }
}
EmbedCardDetails.contextType = RpcContext;

EmbedCardDetails.propTypes = {
  cardId: PropTypes.string.isRequired,
};

export default connect(
  (state, {cardId, frameStyleId}) => ({
    card: selectCardById(state, cardId),
    isLoadingCardFilters: selectIsLoadingCardFilters(state, cardId),
    filters: selectAppliedCombinedFiltersByCardId(state, cardId),
    segments: selectSegmentsByCardId(state, cardId),
    filterGroupIds: selectFilterGroupsByCardId(state, cardId),
    dataControlFilters: selectDataControlFiltersByCardId(state, cardId),
    hasSampleData: selectHasSampleData(state, cardId),
    dateRangeFilter: selectDateRangeFilterByCardId(state, cardId),
    dateTimeElement: selectDateGrainByCardId(state, cardId),
    overrideDateRange: selectOverrideDateRangeByCardId(state, cardId),
    functionOverrides: selectFunctionOverridesByCardId(state, cardId),
    isTitleEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.TITLE),
    isDrillEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.DRILL),
    isDavinci: selectIsDavinci(state),
    layoutFrameStyles: selectFrameStyles(state, frameStyleId),
    hasQuerystringError: selectHasQuerystringError(state, cardId),
  }),
  {
    clearQueryStringError,
    disableChartPicker,
  }
)(EmbedCardDetails);
